import { AvailableMarketPriceGranularity, ConsumptionView, OffPeakRange, PlanConsumptionContentProps } from './types.ts'
import parse from 'html-react-parser'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { Response } from 'types/request.ts'
import { useTranslation } from 'react-i18next'
import { ReactElement, useMemo, useState } from 'react'
import { useGetPriceHistoryQuery } from 'store/queries/bolt-api/metrics'
import { Granularity } from 'types/contracts.ts'
import dayjs from 'dayjs'
import { getIndexOfCurrentPrice, getIndexesOfLowestOrHighestPrices, getOffPeakRanges } from './utils.ts'
import { TransfoErrorCodes } from 'types/billShock.ts'
import LoadingSkeleton from 'components/loading-skeleton/LoadingSkeleton.tsx'
import MarketPricesChart from 'components/charts/market-prices/MarketPricesChart.tsx'
import { Banner, Toggle } from '@boltenergy-be/design-system'
import styles from '../MarketPrices.module.scss'
import { useStoreSelector } from 'hooks/store.ts'
import { trackEvent } from 'utils/tracking.ts'
import { selectIsProducerAccount } from 'store/contact/slice.ts'
import { PriceHistoryType } from 'store/queries/bolt-api/metrics/types.ts'
import { LOWEST_PRICES_HIGHLIGHTED_ENTRIES } from './constants.ts'

const MarketPricesContent = ({ compact, chartLayoutClassName }: PlanConsumptionContentProps) => {
  // Redux state
  const isProducer = useStoreSelector(selectIsProducerAccount)

  // i18n
  const { t } = useTranslation(['marketPrices', 'errors'])

  // Local state
  const [activeView, setActiveView] = useState<ConsumptionView>(ConsumptionView.TODAY)
  const [granularity, setGranularity] = useState<AvailableMarketPriceGranularity>(Granularity.HOUR)

  // Redux Query
  const skipFetchTomorrow = activeView === ConsumptionView.TOMORROW && dayjs().isBefore(dayjs().set('hour', 15))
  const {
    currentData: data,
    isLoading,
    isFetching,
    isError,
    error
  } = useGetPriceHistoryQuery(
    {
      granularity,
      from:
        activeView === ConsumptionView.TODAY ? dayjs().startOf('day').toISOString() : dayjs().startOf('day').add(1, 'day').toISOString(),
      until:
        activeView === ConsumptionView.TODAY
          ? dayjs().endOf('day').add(1, 'day').toISOString()
          : dayjs().endOf('day').add(2, 'day').toISOString(),
      unit: 'kWh',
      type: PriceHistoryType.PRICE_ELECTRICITY
    },
    { skip: skipFetchTomorrow }
  )

  // Constants
  const lowestPriceIndexes = getIndexesOfLowestOrHighestPrices(
    data?.priceHistory.electricity || [],
    LOWEST_PRICES_HIGHLIGHTED_ENTRIES[granularity],
    'low'
  )
  const highestPriceIndexes = getIndexesOfLowestOrHighestPrices(data?.priceHistory.electricity || [], 1, 'high')
  const currentPriceIndex = getIndexOfCurrentPrice(data?.priceHistory.electricity || [], granularity)

  /**
   * Get error message based on error code
   * @param {TransfoErrorCodes | string} code
   * @returns {ReactElement}
   */
  const getErrorMessage = (code?: TransfoErrorCodes | string): ReactElement => {
    if (code === TransfoErrorCodes.E311_NO_DATA_AVAILABLE_FOR_FUTURE_DATES) {
      return <Banner type="informative">{parse(t(($) => $.error.notYetKnown))}</Banner>
    }

    return (
      <Banner type="warning">
        {t(($) => $.tryAgain, {
          ns: 'errors'
        })}
      </Banner>
    )
  }

  // Memoized off-peak hours
  const offPeakHours = useMemo<OffPeakRange[]>(
    () => getOffPeakRanges(data?.priceHistory.electricity || [], lowestPriceIndexes, granularity),
    [data?.priceHistory, lowestPriceIndexes, granularity]
  )

  return (
    <>
      <Toggle
        disabled={isLoading || isFetching}
        active={activeView}
        isFullwidth
        onClick={(view) => {
          trackEvent({ category: 'market_prices', object: 'toggle_view', action: 'click' }, { view })
          setActiveView(view)
        }}
        options={[
          { value: ConsumptionView.TODAY, label: t(($) => $.toggle.today) },
          { value: ConsumptionView.TOMORROW, label: t(($) => $.toggle.tomorrow) }
        ]}
      />

      {!compact &&
        !isProducer &&
        !isError &&
        !skipFetchTomorrow &&
        (isFetching || isLoading ? (
          <LoadingSkeleton>
            <LoadingSkeleton.Rectangle width="100%" height={40} />
          </LoadingSkeleton>
        ) : (
          <Banner type="informative">
            {parse(
              t(($) => $.offPeakHours.text, {
                unit: activeView === ConsumptionView.TODAY ? t(($) => $.toggle.today) : t(($) => $.toggle.tomorrow),

                ranges: offPeakHours.map((range, idx) =>
                  idx === 0
                    ? `${range.from} - ${range.until}`
                    : t(($) => $.offPeakHours.separator, {
                        range: `${range.from} - ${range.until}`
                      })
                )
              }).replace(/,/g, '')
            )}
          </Banner>
        ))}

      {!compact && (
        <Toggle
          active={granularity}
          onClick={(granularity: AvailableMarketPriceGranularity) => setGranularity(granularity)}
          className={styles.toggle}
          optionClassName={styles['toggle-button']}
          radius="full"
          options={[
            {
              label: t(($) => $.granularity.hour),
              value: Granularity.HOUR
            },
            {
              label: t(($) => $.granularity.quarterHour),
              value: Granularity.QUARTER_HOUR
            }
          ]}
        />
      )}

      {/* CHART */}
      {isLoading || isFetching ? (
        <LoadingSkeleton>
          <LoadingSkeleton.Rectangle aspectRatio="3 / 1" />
          <div className="flex-col gap-200">
            <LoadingSkeleton.Rectangle height={20} />
            <LoadingSkeleton.Rectangle height={20} />
          </div>
        </LoadingSkeleton>
      ) : isError || skipFetchTomorrow || !data?.priceHistory.electricity.length ? (
        <>
          {getErrorMessage(
            skipFetchTomorrow
              ? TransfoErrorCodes.E311_NO_DATA_AVAILABLE_FOR_FUTURE_DATES
              : isError
                ? ((error as FetchBaseQueryError).data as Response)?.message
                : 'error'
          )}

          <div className={styles.placeholder} />
        </>
      ) : (
        <MarketPricesChart
          data={data?.priceHistory.electricity || []}
          isCompact={compact}
          layoutClassName={chartLayoutClassName}
          {...{ granularity, lowestPriceIndexes, highestPriceIndexes, currentPriceIndex }}
        />
      )}
    </>
  )
}

export default MarketPricesContent
